import { useMemo, useRef, useState, type ReactElement } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { z } from 'zod';
import { Ban, Check, Eye, Plus, Trash2 } from 'lucide-react';
import { Status } from '@anchorbase/pillar-sdk';
import {
    defaultIntegratedWebsiteDisplayNameBySystemId,
    ThirdPartySystemId,
    ThirdPartySystemIdToName,
    type IpxCredentialDefinitionSearchRequest,
    type IpxWebLoginCredentialDefinitionSearchListItem,
    type OrganizationIpxCredentialPolicyListItem,
    type OrganizationIpxCredentialPolicyResponse,
} from '@anchorbase/pillar-sdk';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { StatusBadge } from '@/components/ui/status-badge';
import {
    InfiniteScrollListEmptyState,
    InfiniteScrollListLoadingState,
} from '@/components/ui/infinite-scroll-list-state';
import { useInfiniteScrollList } from '@/hooks/useInfiniteScrollList';
import { buildRoleScopedQueryKey } from '@/lib/reactQueryRoleScope';
import { useAuthStore } from '@/store/auth';
import { ipxCredentialManagementApi } from '@/lib/pillarApi';
import { IpxCredentialDefinitionOmnisearchSelect } from './IpxCredentialDefinitionOmnisearchSelect';

interface OrganizationIpxCredentialPoliciesCardProps {
    readonly organizationUuid: string;
    readonly defaultCollapsed?: boolean;
}

export const OrganizationIpxCredentialPoliciesCardOpaqueCursorSchema: z.ZodType<string> = z
    .string()
    .trim()
    .min(1);

type PolicyLifecycleAction = 'disable'
    | 'enable'
    | 'delete'
    | 'restore';

export function getIpxCredentialPolicySystemDisplayName(
    policy: Pick<OrganizationIpxCredentialPolicyListItem, 'system_id'>
): string {
    if (policy.system_id === ThirdPartySystemId.GENERIC) {
        return 'Generic Website';
    }
    return (
        defaultIntegratedWebsiteDisplayNameBySystemId[policy.system_id] ??
        ThirdPartySystemIdToName[policy.system_id] ??
        `System ${policy.system_id}`
    );
}

export function formatIpxCredentialPolicyKey(
    policy: Pick<OrganizationIpxCredentialPolicyListItem, 'credential_definition_key'>
): string {
    const trimmedKey: string = policy.credential_definition_key.trim();
    return trimmedKey.length === 0 ? '(default)' : trimmedKey;
}

function getPolicyLifecycleErrorLabel(action: PolicyLifecycleAction): string {
    switch (action) {
        case 'disable':
            return 'disable';
        case 'enable':
            return 'enable';
        case 'delete':
            return 'delete';
        case 'restore':
            return 'restore';
    }
}

function getErrorMessage(error: unknown, fallbackMessage: string): string {
    return error instanceof Error ? error.message : fallbackMessage;
}

export function OrganizationIpxCredentialPoliciesCard(
    props: OrganizationIpxCredentialPoliciesCardProps
): ReactElement {
    const queryClient = useQueryClient();
    const activeRoleAssignmentId: number | null = useAuthStore(
        (state) => state.activeRoleAssignmentId
    );
    const [showDeleted, setShowDeleted] = useState<boolean>(false);
    const [isExpanded, setIsExpanded] = useState<boolean>(
        props.defaultCollapsed !== true
    );
    const scrollContainerRef = useRef<HTMLDivElement | null>(null);
    const [canCreatePolicy, setCanCreatePolicy] = useState<boolean>(false);
    const [addDialogOpen, setAddDialogOpen] = useState<boolean>(false);
    const [selectedDefinition, setSelectedDefinition] =
        useState<IpxWebLoginCredentialDefinitionSearchListItem | null>(null);
    const [policyToDelete, setPolicyToDelete] =
        useState<OrganizationIpxCredentialPolicyListItem | null>(null);

    const policiesQueryKey = useMemo(
        () =>
            buildRoleScopedQueryKey(activeRoleAssignmentId, [
                'ipx-credential-policies',
                props.organizationUuid,
                showDeleted ? Status.DELETED : 'not-deleted',
            ]),
        [activeRoleAssignmentId, props.organizationUuid, showDeleted]
    );
    const policiesInvalidationKey = useMemo(
        () =>
            buildRoleScopedQueryKey(activeRoleAssignmentId, [
                'ipx-credential-policies',
                props.organizationUuid,
            ]),
        [activeRoleAssignmentId, props.organizationUuid]
    );
    const fetchPolicyDefinitionCandidates = useMemo(
        () =>
            async (request: IpxCredentialDefinitionSearchRequest) => {
                try {
                    return await ipxCredentialManagementApi.searchOrganizationCredentialPolicyDefinitions(
                        {
                            ...request,
                            organization_uuid: props.organizationUuid,
                        }
                    );
                } catch {
                    throw new Error(
                        'Unable to load credential definitions. Try again.'
                    );
                }
            },
        [props.organizationUuid]
    );

    const policiesList = useInfiniteScrollList<
        OrganizationIpxCredentialPolicyListItem,
        string,
        readonly unknown[]
    >({
        enabled: isExpanded,
        queryKeyPrefix: policiesQueryKey,
        pageSize: 50,
        observerRootRef: scrollContainerRef,
        rootMargin: '1200px 0px',
        parseCursorParam: (pageParam: unknown) => {
            if (pageParam === null || pageParam === undefined) {
                return undefined;
            }
            const parseResult =
                OrganizationIpxCredentialPoliciesCardOpaqueCursorSchema.safeParse(
                    pageParam
                );
            if (!parseResult.success) {
                throw new Error('Expected opaque cursor');
            }
            return parseResult.data;
        },
        fetchPage: async ({ limit, cursor }) => {
            const response =
                await ipxCredentialManagementApi.listOrganizationCredentialPolicies({
                    organization_uuid: props.organizationUuid,
                    ...(showDeleted ? { status: Status.DELETED } : {}),
                    limit,
                    cursor,
                });
            setCanCreatePolicy(response.capabilities.can_create);
            return {
                items: response.policies,
                pagination: response.pagination ?? { has_more: false },
            };
        },
    });

    const invalidatePolicies = async (): Promise<void> => {
        await queryClient.invalidateQueries({
            queryKey: policiesInvalidationKey,
        });
    };

    const createMutation = useMutation({
        mutationFn: async (
            definition: IpxWebLoginCredentialDefinitionSearchListItem
        ) =>
            await ipxCredentialManagementApi.createOrganizationCredentialPolicy({
                organization_uuid: props.organizationUuid,
                ipx_credential_definition_uuid:
                    definition.ipx_credential_definition_uuid,
            }),
        onSuccess: async () => {
            setAddDialogOpen(false);
            setSelectedDefinition(null);
            await invalidatePolicies();
        },
    });

    const lifecycleMutation = useMutation({
        mutationFn: async (variables: {
            readonly action: PolicyLifecycleAction;
            readonly policy: OrganizationIpxCredentialPolicyListItem;
        }): Promise<OrganizationIpxCredentialPolicyResponse> => {
            const request = {
                organization_uuid: props.organizationUuid,
                organization_ipx_credential_policy_uuid:
                    variables.policy.organization_ipx_credential_policy_uuid,
            };
            switch (variables.action) {
                case 'disable':
                    return await ipxCredentialManagementApi.disableOrganizationCredentialPolicy(
                        request
                    );
                case 'enable':
                    return await ipxCredentialManagementApi.enableOrganizationCredentialPolicy(
                        request
                    );
                case 'delete':
                    return await ipxCredentialManagementApi.deleteOrganizationCredentialPolicy(
                        request
                    );
                case 'restore':
                    return await ipxCredentialManagementApi.restoreOrganizationCredentialPolicy(
                        request
                    );
            }
        },
        onSuccess: async () => {
            setPolicyToDelete(null);
            await invalidatePolicies();
        },
    });

    const policies: readonly OrganizationIpxCredentialPolicyListItem[] =
        policiesList.items;
    const lifecycleActionLabel: string | null =
        lifecycleMutation.variables === undefined
            ? null
            : getPolicyLifecycleErrorLabel(lifecycleMutation.variables.action);

    return (
        <>
            <Card className="mb-6">
                <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle>Credential Policies</CardTitle>
                    <div className="flex items-center gap-4">
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => {
                                setIsExpanded((currentValue) => !currentValue);
                            }}
                        >
                            {isExpanded ? 'Collapse' : 'Expand'}
                        </Button>
                        {isExpanded ? (
                            <div className="flex items-center gap-2">
                                <Label
                                    htmlFor="credential-policies-show-deleted"
                                    className="cursor-pointer"
                                >
                                    Show deleted
                                </Label>
                                <Checkbox
                                    id="credential-policies-show-deleted"
                                    checked={showDeleted}
                                    onCheckedChange={(checked) => {
                                        setShowDeleted(checked === true);
                                    }}
                                />
                            </div>
                        ) : null}
                        {isExpanded && canCreatePolicy ? (
                            <Button
                                size="sm"
                                onClick={() => {
                                    setAddDialogOpen(true);
                                }}
                            >
                                <Plus className="h-4 w-4 mr-2" />
                                Add Credential Policy
                            </Button>
                        ) : null}
                    </div>
                </CardHeader>
                {isExpanded ? (
                <CardContent>
                    {policiesList.error !== null ? (
                        <Alert variant="destructive" className="mb-4">
                            <AlertDescription>
                                {getErrorMessage(
                                    policiesList.error,
                                    'Failed to load credential policies'
                                )}
                            </AlertDescription>
                        </Alert>
                    ) : null}
                    {createMutation.error ? (
                        <Alert variant="destructive" className="mb-4">
                            <AlertDescription>
                                {getErrorMessage(
                                    createMutation.error,
                                    'Failed to create credential policy'
                                )}
                            </AlertDescription>
                        </Alert>
                    ) : null}
                    {lifecycleMutation.error ? (
                        <Alert variant="destructive" className="mb-4">
                            <AlertDescription>
                                {getErrorMessage(
                                    lifecycleMutation.error,
                                    lifecycleActionLabel === null
                                        ? 'Failed to update credential policy'
                                        : `Failed to ${lifecycleActionLabel} credential policy`
                                )}
                            </AlertDescription>
                        </Alert>
                    ) : null}

                    {policiesList.isLoading && policies.length === 0 ? (
                        <InfiniteScrollListLoadingState label="Loading credential policies..." />
                    ) : policies.length === 0 ? (
                        <InfiniteScrollListEmptyState label="No credential policies configured for this organization." />
                    ) : (
                        <div
                            ref={scrollContainerRef}
                            className="max-h-[42rem] space-y-3 overflow-y-auto pr-1"
                        >
                            {policies.map((policy) => (
                                <div
                                    key={
                                        policy.organization_ipx_credential_policy_uuid
                                    }
                                    className="border rounded p-4 flex items-start justify-between gap-4"
                                >
                                    <div className="space-y-1">
                                        <div className="flex items-center gap-2">
                                            <div className="font-medium">
                                                {
                                                    policy.ipx_credential_definition_name
                                                }
                                            </div>
                                            <StatusBadge
                                                status={policy.status}
                                            />
                                        </div>
                                        <div className="text-sm text-muted-foreground">
                                            {getIpxCredentialPolicySystemDisplayName(
                                                policy
                                            )}
                                        </div>
                                        <div className="text-sm text-muted-foreground">
                                                Key: {formatIpxCredentialPolicyKey(
                                                policy
                                            )}
                                        </div>
                                        <div className="text-sm text-muted-foreground">
                                            Credentials: {policy.credential_count}
                                        </div>
                                        <div className="text-sm text-muted-foreground">
                                            Missing personal credentials:{' '}
                                            {
                                                policy.missing_personal_credential_count
                                            }
                                        </div>
                                    </div>
                                    <div className="flex gap-2">
                                        {policy.capabilities.can_view ? (
                                            <Button
                                                asChild
                                                variant="outline"
                                                size="sm"
                                                title="View credential policy"
                                            >
                                                <Link
                                                    to={`/organizations/${props.organizationUuid}/credential-policies/${policy.organization_ipx_credential_policy_uuid}`}
                                                >
                                                    <Eye className="h-4 w-4" />
                                                </Link>
                                            </Button>
                                        ) : null}
                                        {policy.status === Status.ACTIVE &&
                                        policy.capabilities.can_disable ? (
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                title="Disable credential policy"
                                                onClick={() =>
                                                    lifecycleMutation.mutate({
                                                        action: 'disable',
                                                        policy,
                                                    })
                                                }
                                            >
                                                <Ban className="h-4 w-4" />
                                            </Button>
                                        ) : null}
                                        {policy.status === Status.DISABLED &&
                                        policy.capabilities.can_enable ? (
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                title="Enable credential policy"
                                                onClick={() =>
                                                    lifecycleMutation.mutate({
                                                        action: 'enable',
                                                        policy,
                                                    })
                                                }
                                            >
                                                <Check className="h-4 w-4" />
                                            </Button>
                                        ) : null}
                                        {policy.status === Status.DELETED &&
                                        policy.capabilities.can_restore ? (
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                title="Restore credential policy"
                                                onClick={() =>
                                                    lifecycleMutation.mutate({
                                                        action: 'restore',
                                                        policy,
                                                    })
                                                }
                                            >
                                                <Check className="h-4 w-4" />
                                            </Button>
                                        ) : null}
                                        {policy.status !== Status.DELETED &&
                                        policy.capabilities.can_delete ? (
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                title="Delete credential policy"
                                                onClick={() => {
                                                    setPolicyToDelete(policy);
                                                }}
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </Button>
                                        ) : null}
                                    </div>
                                </div>
                            ))}
                            <div
                                ref={policiesList.loadMoreSentinelRef}
                                className="h-8"
                            />
                            {policiesList.isFetching ? (
                                <InfiniteScrollListLoadingState label="Loading credential policies..." />
                            ) : null}
                        </div>
                    )}
                </CardContent>
                ) : null}
            </Card>

            <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Add Credential Policy</DialogTitle>
                        <DialogDescription>
                            Select the integrated website credential definition this organization
                            should use.
                        </DialogDescription>
                    </DialogHeader>
                    <IpxCredentialDefinitionOmnisearchSelect
                        selectedDefinition={selectedDefinition}
                        selectedDefinitionUuid={
                            selectedDefinition?.ipx_credential_definition_uuid ??
                            null
                        }
                        onSelect={setSelectedDefinition}
                        onClearSelection={() => {
                            setSelectedDefinition(null);
                        }}
                        status={Status.ACTIVE}
                        fetchDefinitionsPage={fetchPolicyDefinitionCandidates}
                        resetToken={addDialogOpen ? 'open' : 'closed'}
                    />
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => {
                                setAddDialogOpen(false);
                                setSelectedDefinition(null);
                            }}
                            disabled={createMutation.isPending}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={() => {
                                if (selectedDefinition === null) {
                                    return;
                                }
                                createMutation.mutate(selectedDefinition);
                            }}
                            disabled={
                                selectedDefinition === null ||
                                createMutation.isPending
                            }
                            title={
                                selectedDefinition === null
                                    ? 'Select a credential definition before creating a policy'
                                    : undefined
                            }
                        >
                            {createMutation.isPending ? 'Creating...' : 'Create'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog
                open={policyToDelete !== null}
                onOpenChange={(isOpen) => {
                    if (!isOpen) {
                        setPolicyToDelete(null);
                    }
                }}
            >
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Delete Credential Policy</DialogTitle>
                        <DialogDescription>
                            {policyToDelete === null
                                ? 'Delete this credential policy?'
                                : `Delete credential policy for ${policyToDelete.ipx_credential_definition_name}?`}
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => {
                                setPolicyToDelete(null);
                            }}
                            disabled={lifecycleMutation.isPending}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={() => {
                                if (policyToDelete === null) {
                                    return;
                                }
                                lifecycleMutation.mutate({
                                    action: 'delete',
                                    policy: policyToDelete,
                                });
                            }}
                            disabled={
                                policyToDelete === null ||
                                lifecycleMutation.isPending
                            }
                        >
                            {lifecycleMutation.isPending
                                ? 'Deleting...'
                                : 'Delete'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
